// ============================================================
// EditReceiptModal.tsx
// ------------------------------------------------------------
// 2026-05-21: Manuell edit-modal fr enskilda kvitton.
// ppnas frn Receipts-vyn nr anvndaren klickar p en rad.
//
// Visar form med ALLA editerbara flt + PDF-preview till hger.
// Anvnder updateReceiptFields() frn cloudStore som automatiskt
// stter vatStatus='manual' nr moms-flt sparas.
//
// Inspirerad av MomsBalansApp/src/screens/main/EditPostScreen.tsx
// (React Native) men helt re-implementerad fr webb + DOM.
// ============================================================

import { useEffect, useMemo, useRef, useState } from 'react'
import { PreviewPane } from './PreviewPane'
import {
  updateReceiptFields,
  deleteReceiptCloud,
  aiAnalyzeReceipt,
  type CloudReceipt,
} from '../lib/cloudStore'

const fmt = (n: number) => n.toLocaleString('sv-SE', { maximumFractionDigits: 2 })

const CAT_LABEL: Record<CloudReceipt['category'], string> = {
  kundfakturor: 'Kundfaktura',
  leverantorsfakturor: 'Leverantrsfaktura',
  utlagg: 'Utlgg',
}

const CAT_COLOR: Record<CloudReceipt['category'], string> = {
  kundfakturor: '#10b981',
  leverantorsfakturor: '#ef4444',
  utlagg: '#3b82f6',
}

const VAT_STATUS_LABEL: Record<NonNullable<CloudReceipt['vatStatus']>, string> = {
  ai_extracted: 'AI-extraherad',
  manual: 'Manuellt ifylld',
  sie_import: 'SIE-import',
  missing: 'Saknas',
}

const VAT_STATUS_COLOR: Record<NonNullable<CloudReceipt['vatStatus']>, string> = {
  ai_extracted: '#3b82f6',
  manual: '#10b981',
  sie_import: '#8b5cf6',
  missing: '#f59e0b',
}

const COMMON_VAT_RATES = [
  { v: 0, label: '0% (utlndskt / momsbefriat)' },
  { v: 0.06, label: '6% (bcker, tidskrifter, transport)' },
  { v: 0.12, label: '12% (livsmedel, hotell)' },
  { v: 0.25, label: '25% (standard)' },
]

interface Props {
  receipt: CloudReceipt
  companyId: string
  onClose: () => void
  onDeleted?: () => void
}

/** Konvertera valuestring till nummer (svenska + engelska decimal-tecken).
 *  Returnerar undefined om input r tomt eller ogiltigt (NaN).
 */
function parseNum(s: string | undefined): number | undefined {
  if (!s) return undefined
  const t = s.trim()
  if (!t) return undefined
  const n = parseFloat(t.replace(/\s/g, '').replace(',', '.'))
  return isNaN(n) ? undefined : n
}

export function EditReceiptModal({ receipt, companyId, onClose, onDeleted }: Props) {
  // Form state — initieras frn receipt
  const [date, setDate] = useState(receipt.date ?? '')
  const [vendor, setVendor] = useState(receipt.title ?? '')
  const [amount, setAmount] = useState(receipt.amount != null ? String(receipt.amount) : '')
  const [amountExVat, setAmountExVat] = useState(receipt.amountExVat != null ? String(receipt.amountExVat) : '')
  const [vatAmount, setVatAmount] = useState(receipt.vatAmount != null ? String(receipt.vatAmount) : '')
  const [vatRate, setVatRate] = useState(receipt.vatRate != null ? String(receipt.vatRate * 100) : '')
  const [currency, setCurrency] = useState(receipt.currency ?? 'SEK')
  const [category, setCategory] = useState<CloudReceipt['category']>(receipt.category)
  const [accountCode, setAccountCode] = useState(receipt.accountCode != null ? String(receipt.accountCode) : '')
  const [accountLabel, setAccountLabel] = useState(receipt.accountLabel ?? '')

  // Auto-compute-toggle: nr p, rkna vatAmount frn amount+vatRate automatiskt
  const [autoCompute, setAutoCompute] = useState(true)
  // Sprra mot oavsiktlig overwrite — markera vilket flt som "drives"
  const lastEditedRef = useRef<'amount' | 'amountExVat' | 'vatAmount' | 'vatRate' | null>(null)

  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)
  // 2026-05-21 (Re-run AI feature): pgende AI-anrop p denna post
  const [aiRunning, setAiRunning] = useState(false)

  // Synca frn receipt nr Firestore uppdaterar i bakgrunden
  useEffect(() => {
    setDate(receipt.date ?? '')
    setVendor(receipt.title ?? '')
    setAmount(receipt.amount != null ? String(receipt.amount) : '')
    setAmountExVat(receipt.amountExVat != null ? String(receipt.amountExVat) : '')
    setVatAmount(receipt.vatAmount != null ? String(receipt.vatAmount) : '')
    setVatRate(receipt.vatRate != null ? String(receipt.vatRate * 100) : '')
    setCurrency(receipt.currency ?? 'SEK')
    setCategory(receipt.category)
    setAccountCode(receipt.accountCode != null ? String(receipt.accountCode) : '')
    setAccountLabel(receipt.accountLabel ?? '')
  }, [
    receipt.id, receipt.date, receipt.title, receipt.amount, receipt.amountExVat,
    receipt.vatAmount, receipt.vatRate, receipt.currency, receipt.category,
    receipt.accountCode, receipt.accountLabel,
  ])

  // Escape stnger
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  // Auto-compute moms-belopp utifrn vilka flt anvndaren senast rrde
  useEffect(() => {
    if (!autoCompute) return
    const amt = parseNum(amount)
    const exVat = parseNum(amountExVat)
    const rate = parseNum(vatRate)
    const vatNum = parseNum(vatAmount)

    // Case 1: amount + vatRate angivna → rkna ut vatAmount (inkl moms-flde)
    if (lastEditedRef.current === 'amount' || lastEditedRef.current === 'vatRate') {
      if (amt != null && !isNaN(amt) && rate != null && !isNaN(rate) && rate >= 0) {
        const decimalRate = rate >= 1 ? rate / 100 : rate
        const computedVat = decimalRate > 0
          ? amt * decimalRate / (1 + decimalRate)
          : 0
        const computedExVat = amt - computedVat
        const newVatStr = computedVat.toFixed(2)
        const newExVatStr = computedExVat.toFixed(2)
        if (newVatStr !== vatAmount) setVatAmount(newVatStr)
        if (newExVatStr !== amountExVat) setAmountExVat(newExVatStr)
      }
      return
    }

    // Case 2: amountExVat + vatRate → vat = exVat * rate
    if (lastEditedRef.current === 'amountExVat') {
      if (exVat != null && !isNaN(exVat) && rate != null && !isNaN(rate) && rate >= 0) {
        const decimalRate = rate >= 1 ? rate / 100 : rate
        const computedVat = exVat * decimalRate
        const computedAmt = exVat + computedVat
        const newVatStr = computedVat.toFixed(2)
        const newAmtStr = computedAmt.toFixed(2)
        if (newVatStr !== vatAmount) setVatAmount(newVatStr)
        if (newAmtStr !== amount) setAmount(newAmtStr)
      }
      return
    }

    // Case 3: vatAmount + amount → rkna ut implicit rate
    if (lastEditedRef.current === 'vatAmount') {
      if (vatNum != null && !isNaN(vatNum) && amt != null && !isNaN(amt) && amt > 0) {
        const ex = amt - vatNum
        const newRate = ex > 0 ? (vatNum / ex) * 100 : 0
        const newRateStr = newRate.toFixed(2)
        const newExStr = ex.toFixed(2)
        if (newRateStr !== vatRate) setVatRate(newRateStr)
        if (newExStr !== amountExVat) setAmountExVat(newExStr)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, amountExVat, vatAmount, vatRate, autoCompute])

  const isDirty = useMemo(() => {
    return (
      date !== (receipt.date ?? '') ||
      vendor !== (receipt.title ?? '') ||
      amount !== (receipt.amount != null ? String(receipt.amount) : '') ||
      currency !== (receipt.currency ?? 'SEK') ||
      amountExVat !== (receipt.amountExVat != null ? String(receipt.amountExVat) : '') ||
      vatAmount !== (receipt.vatAmount != null ? String(receipt.vatAmount) : '') ||
      vatRate !== (receipt.vatRate != null ? String(receipt.vatRate * 100) : '') ||
      category !== receipt.category ||
      accountCode !== (receipt.accountCode != null ? String(receipt.accountCode) : '') ||
      accountLabel !== (receipt.accountLabel ?? '')
    )
  }, [
    receipt, date, vendor, amount, currency, amountExVat,
    vatAmount, vatRate, category, accountCode, accountLabel,
  ])

  async function onSave() {
    setSaving(true)
    setMsg(null)
    try {
      const amt = parseNum(amount)
      const exVat = parseNum(amountExVat)
      const vatNum = parseNum(vatAmount)
      const rateNum = parseNum(vatRate)
      const acc = parseNum(accountCode)

      // Valideringar
      if (amt != null && (isNaN(amt) || amt < 0)) throw new Error('Belopp r ogiltigt')
      if (exVat != null && isNaN(exVat)) throw new Error('Belopp exkl moms r ogiltigt')
      if (vatNum != null && isNaN(vatNum)) throw new Error('Momsbelopp r ogiltigt')
      if (rateNum != null && (isNaN(rateNum) || rateNum < 0 || rateNum > 100)) throw new Error('Momssats mste vara 0-100%')

      const rate = rateNum != null ? (rateNum >= 1 ? rateNum / 100 : rateNum) : undefined

      await updateReceiptFields(companyId, receipt.id, {
        date: date || undefined,
        title: vendor || undefined,
        amount: amt,
        currency: currency || undefined,
        amountExVat: exVat,
        vatAmount: vatNum,
        vatRate: rate,
        accountCode: acc != null && !isNaN(acc) ? Math.round(acc) : undefined,
        accountLabel: accountLabel || undefined,
      })

      setMsg({ kind: 'ok', text: 'Sparat!' })
      setTimeout(() => setMsg(null), 2000)
    } catch (e) {
      setMsg({ kind: 'err', text: 'Fel: ' + (e as Error).message })
    } finally {
      setSaving(false)
    }
  }

  async function onRunAi() {
    if (!receipt.storagePath) {
      setMsg({ kind: 'err', text: 'Posten har ingen PDF i Storage — kr AI-batchen frst' })
      return
    }
    setAiRunning(true)
    setMsg(null)
    try {
      const res = await aiAnalyzeReceipt(companyId, receipt, true)
      if (res.error && !res.updated) {
        setMsg({ kind: 'err', text: 'AI: ' + res.error })
      } else {
        setMsg({ kind: 'ok', text: 'AI klar — flten uppdaterade (granska och spara)' })
        setTimeout(() => setMsg(null), 3000)
      }
      // Firestore-prenumerationen kommer mata in nya vrden i receipt-propsen
      // som useEffect ovan synkar till formen.
    } catch (e) {
      setMsg({ kind: 'err', text: 'AI-fel: ' + (e as Error).message })
    } finally {
      setAiRunning(false)
    }
  }

  async function onDelete() {
    if (!confirm('Radera ' + receipt.filename + '?\n\nFil tas bort frn Storage och post frn Firestore.')) return
    try {
      await deleteReceiptCloud(companyId, receipt)
      onDeleted?.()
      onClose()
    } catch (e) {
      setMsg({ kind: 'err', text: 'Fel: ' + (e as Error).message })
    }
  }

  const vatStatusKey: NonNullable<CloudReceipt['vatStatus']> = receipt.vatStatus ?? 'missing'

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1100, padding: '1.5rem',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: 10,
          width: 'min(1100px, 100%)',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          color: 'var(--text)',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '0.75rem 1rem',
          borderBottom: '1px solid var(--border)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexShrink: 0,
        }}>
          <div>
            <div style={{ fontSize: '1rem', fontWeight: 600 }}>Redigera kvitto</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }} title={receipt.filename}>
              {receipt.filename.length > 80 ? receipt.filename.slice(0, 80) + '...' : receipt.filename}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', color: 'var(--text-muted)',
              fontSize: '1.3rem', cursor: 'pointer', padding: '0.25rem 0.5rem',
            }}
            aria-label="Stng"
          >{'✕'}</button>
        </div>

        {/* Body: Two-column layout */}
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
          {/* Vnster: Form */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '1rem', minWidth: 0 }}>
            {/* Status-rad */}
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
              <span style={{
                background: CAT_COLOR[receipt.category] + '22',
                color: CAT_COLOR[receipt.category],
                fontSize: '0.7rem',
                padding: '2px 8px',
                borderRadius: 4,
                fontWeight: 600,
              }}>{CAT_LABEL[receipt.category]}</span>
              <span style={{
                background: VAT_STATUS_COLOR[vatStatusKey] + '22',
                color: VAT_STATUS_COLOR[vatStatusKey],
                fontSize: '0.7rem',
                padding: '2px 8px',
                borderRadius: 4,
                fontWeight: 600,
              }} title="vatStatus stts automatiskt av spar-logiken">
                Moms-klla: {VAT_STATUS_LABEL[vatStatusKey]}
              </span>
              {receipt.quality && (
                <span style={{
                  background: 'rgba(255,255,255,0.06)',
                  color: 'var(--text-muted)',
                  fontSize: '0.7rem',
                  padding: '2px 8px',
                  borderRadius: 4,
                }}>Kvalitet: {receipt.quality}</span>
              )}
            </div>

            {/* 2026-05-21 (Re-run AI): kor om AI pa denna PDF direkt frn modalen.
                Krver att posten har storagePath. Anvndaren kan sedan granska
                och spara de nya vrdena. */}
            {receipt.storagePath && (
              <div style={{
                marginBottom: '0.75rem',
                padding: '0.45rem 0.7rem',
                background: 'rgba(59,130,246,0.10)',
                border: '1px solid #3b82f6',
                borderRadius: 6,
                display: 'flex',
                alignItems: 'center',
                gap: '0.6rem',
                flexWrap: 'wrap',
              }}>
                <span style={{ fontSize: '0.78rem', color: 'var(--text)', flex: 1, minWidth: 180 }}>
                  {'📄 PDF finns i Storage — AI kan hmta filen och fylla i flten'}
                </span>
                <button
                  type="button"
                  onClick={onRunAi}
                  disabled={aiRunning}
                  style={{
                    background: aiRunning ? 'rgba(59,130,246,0.5)' : '#3b82f6',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 4,
                    padding: '0.3rem 0.7rem',
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    cursor: aiRunning ? 'not-allowed' : 'pointer',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.3rem',
                  }}
                >
                  {aiRunning ? (
                    <>
                      <span style={{
                        display: 'inline-block',
                        width: 10, height: 10,
                        border: '2px solid #fff',
                        borderTopColor: 'transparent',
                        borderRadius: '50%',
                        animation: 'editReceiptSpin 0.8s linear infinite',
                      }} />
                      {'Kr...'}
                    </>
                  ) : (
                    '🔁 Kr AI p denna PDF'
                  )}
                </button>
                <style>{'@keyframes editReceiptSpin { to { transform: rotate(360deg); } }'}</style>
              </div>
            )}

            {/* Fltgrupp: Allmnt */}
            <Section title="Allmnt">
              <Row>
                <Field label="Datum">
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    style={inputStyle}
                  />
                  {receipt.storagePath && (
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 2 }}>
                      ⚠️ Ändrar år/månad flyttar EJ PDF:n i Storage (storagePath behålls gammal — se cloudStore NOTE). Ladda upp igen vid behov.
                    </div>
                  )}
                </Field>
                <Field label="Kategori">
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value as CloudReceipt['category'])}
                    style={inputStyle}
                  >
                    <option value="kundfakturor">Kundfakturor</option>
                    <option value="leverantorsfakturor">Leverantrsfakturor</option>
                    <option value="utlagg">Utlgg</option>
                  </select>
                </Field>
              </Row>
              <Row>
                <Field label="Leverantr / titel" full>
                  <input
                    type="text"
                    value={vendor}
                    onChange={(e) => setVendor(e.target.value)}
                    placeholder="t.ex. Cisco Meraki / Hetzner / Anthropic"
                    style={inputStyle}
                  />
                </Field>
              </Row>
            </Section>

            {/* Fltgrupp: Belopp + moms */}
            <Section title="Belopp och moms">
              <div style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                fontSize: '0.78rem', color: 'var(--text-muted)',
                marginBottom: '0.5rem',
              }}>
                <input
                  type="checkbox"
                  checked={autoCompute}
                  onChange={(e) => setAutoCompute(e.target.checked)}
                  id="autocompute"
                />
                <label htmlFor="autocompute">Auto-rkna: belopp + momssats {'→'} momsbelopp + exkl-moms</label>
              </div>
              <Row>
                <Field label="Belopp (inkl moms)">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={amount}
                    onChange={(e) => { lastEditedRef.current = 'amount'; setAmount(e.target.value) }}
                    placeholder="0"
                    style={inputStyle}
                  />
                </Field>
                <Field label="Valuta">
                  <select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    style={inputStyle}
                  >
                    <option value="SEK">SEK</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="NOK">NOK</option>
                    <option value="DKK">DKK</option>
                  </select>
                </Field>
              </Row>
              <Row>
                <Field label="Exkl. moms">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={amountExVat}
                    onChange={(e) => { lastEditedRef.current = 'amountExVat'; setAmountExVat(e.target.value) }}
                    placeholder="0"
                    style={inputStyle}
                  />
                </Field>
                <Field label="Momsbelopp">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={vatAmount}
                    onChange={(e) => { lastEditedRef.current = 'vatAmount'; setVatAmount(e.target.value) }}
                    placeholder="0"
                    style={inputStyle}
                  />
                </Field>
              </Row>
              <Row>
                <Field label="Momssats %">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={vatRate}
                    onChange={(e) => { lastEditedRef.current = 'vatRate'; setVatRate(e.target.value) }}
                    placeholder="25"
                    style={inputStyle}
                  />
                </Field>
                <Field label="Snabbval">
                  <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                    {COMMON_VAT_RATES.map((r) => (
                      <button
                        key={r.v}
                        type="button"
                        onClick={() => {
                          lastEditedRef.current = 'vatRate'
                          setVatRate(String(r.v * 100))
                        }}
                        title={r.label}
                        style={{
                          background: 'rgba(255,255,255,0.06)',
                          color: 'var(--text)',
                          border: '1px solid var(--border)',
                          borderRadius: 4,
                          padding: '0.2rem 0.45rem',
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                        }}
                      >{(r.v * 100).toFixed(0)}%</button>
                    ))}
                  </div>
                </Field>
              </Row>
            </Section>

            {/* Fltgrupp: BAS-konto */}
            <Section title="BAS-konto (bokfring)">
              <Row>
                <Field label="Kontonr">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={accountCode}
                    onChange={(e) => setAccountCode(e.target.value)}
                    placeholder="5410"
                    style={inputStyle}
                  />
                </Field>
                <Field label="Kontobeskrivning">
                  <input
                    type="text"
                    value={accountLabel}
                    onChange={(e) => setAccountLabel(e.target.value)}
                    placeholder="Frbrukningsinventarier"
                    style={inputStyle}
                  />
                </Field>
              </Row>
            </Section>

            {/* Sammanfattning */}
            {parseNum(amount) != null && !isNaN(parseNum(amount)!) && (
              <div style={{
                background: 'rgba(59,130,246,0.08)',
                border: '1px solid #3b82f6',
                borderRadius: 6,
                padding: '0.5rem 0.75rem',
                fontSize: '0.82rem',
                color: 'var(--text-muted)',
                marginTop: '0.5rem',
              }}>
                <div><b style={{ color: 'var(--text)' }}>Belopp:</b> {fmt(parseNum(amount)!)} {currency}</div>
                {parseNum(amountExVat) != null && <div><b style={{ color: 'var(--text)' }}>Exkl moms:</b> {fmt(parseNum(amountExVat)!)} {currency}</div>}
                {parseNum(vatAmount) != null && <div><b style={{ color: 'var(--text)' }}>Moms:</b> {fmt(parseNum(vatAmount)!)} {currency} ({vatRate || '?'}%)</div>}
              </div>
            )}

            {/* Meddelande */}
            {msg && (
              <div style={{
                marginTop: '0.75rem',
                padding: '0.5rem 0.75rem',
                borderRadius: 6,
                fontSize: '0.85rem',
                background: msg.kind === 'err' ? 'rgba(239,68,68,0.10)' : 'rgba(16,185,129,0.10)',
                color: msg.kind === 'err' ? '#ef4444' : '#10b981',
                border: '1px solid ' + (msg.kind === 'err' ? '#ef4444' : '#10b981'),
              }}>{msg.text}</div>
            )}
          </div>

          {/* Hger: PDF-preview */}
          <div style={{
            width: '50%', maxWidth: 540, minWidth: 320,
            borderLeft: '1px solid var(--border)',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}>
            <div style={{
              padding: '0.4rem 0.75rem',
              fontSize: '0.75rem',
              color: 'var(--text-muted)',
              borderBottom: '1px solid var(--border)',
              background: 'rgba(0,0,0,0.2)',
            }}>{'Frhandsgranskning'}</div>
            <PreviewPane
              url={receipt.downloadUrl}
              mime={receipt.mime}
              filename={receipt.filename}
              sourcePath={receipt.sourcePath}
              background="#0f172a"
            />
          </div>
        </div>

        {/* Footer-knappar */}
        <div style={{
          padding: '0.6rem 1rem',
          borderTop: '1px solid var(--border)',
          display: 'flex', gap: '0.5rem', alignItems: 'center',
          flexShrink: 0, background: 'rgba(0,0,0,0.15)',
        }}>
          <button
            onClick={onDelete}
            style={{
              background: 'rgba(239,68,68,0.10)',
              color: '#ef4444',
              border: '1px solid #ef4444',
              borderRadius: 6, padding: '0.45rem 0.9rem',
              fontSize: '0.85rem', cursor: 'pointer',
            }}
          >{'Ta bort'}</button>
          {receipt.downloadUrl && (
            <a
              href={receipt.downloadUrl}
              target="_blank"
              rel="noopener"
              style={{
                background: 'rgba(255,255,255,0.06)', color: 'var(--text)',
                border: '1px solid var(--border)', borderRadius: 6,
                padding: '0.45rem 0.9rem', fontSize: '0.85rem',
                textDecoration: 'none',
              }}
            >{'Öppna original ↗'}</a>
          )}
          <span style={{ flex: 1 }} />
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.06)', color: 'var(--text)',
              border: '1px solid var(--border)', borderRadius: 6,
              padding: '0.45rem 0.9rem', fontSize: '0.85rem', cursor: 'pointer',
            }}
          >{'Avbryt'}</button>
          <button
            onClick={onSave}
            disabled={!isDirty || saving}
            style={{
              background: isDirty ? '#10b981' : 'rgba(255,255,255,0.10)',
              color: '#fff', border: 'none', borderRadius: 6,
              padding: '0.45rem 1.1rem', fontSize: '0.9rem', fontWeight: 600,
              cursor: !isDirty || saving ? 'not-allowed' : 'pointer',
              opacity: !isDirty || saving ? 0.5 : 1,
            }}
          >{saving ? 'Sparar...' : 'Spara'}</button>
        </div>
      </div>
    </div>
  )
}

// --- Helpers ---

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{
      marginBottom: '0.75rem',
      padding: '0.6rem 0.75rem',
      background: 'rgba(255,255,255,0.02)',
      border: '1px solid var(--border)',
      borderRadius: 6,
    }}>
      <div style={{
        fontSize: '0.72rem',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: 'var(--text-muted)',
        marginBottom: '0.4rem',
        fontWeight: 600,
      }}>{title}</div>
      {children}
    </div>
  )
}

function Row({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.4rem' }}>
      {children}
    </div>
  )
}

function Field({ label, full, children }: { label: string; full?: boolean; children: React.ReactNode }) {
  return (
    <div style={full ? { gridColumn: 'span 2' } : undefined}>
      <label style={{
        display: 'block',
        fontSize: '0.7rem',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        color: 'var(--text-muted)',
        marginBottom: 3,
      }}>{label}</label>
      {children}
    </div>
  )
}

const inputStyle: React.CSSProperties = {
  background: 'var(--bg-card-alt)',
  color: 'var(--text)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  padding: '0.4rem 0.6rem',
  fontSize: '0.9rem',
  width: '100%',
  boxSizing: 'border-box',
}

export default EditReceiptModal
